import { GameMode } from '@/contexts/GameModeContext';
import { GameStatus, GuessHistory, ValidatedGuess, KeyboardStatus, PreviousWord } from '@/lib/types/GameTypes';
import * as CookieUtils from '@/lib/cookieUtils';

/**
 * Mode-aware storage manager that enforces strict mode isolation
 * Each mode gets completely separate storage namespace
 */
export class ModeAwareStorageManager {
  private static instance: ModeAwareStorageManager;
  
  private constructor() {}
  
  static getInstance(): ModeAwareStorageManager {
    if (!ModeAwareStorageManager.instance) {
      ModeAwareStorageManager.instance = new ModeAwareStorageManager();
    }
    return ModeAwareStorageManager.instance;
  }

  /**
   * Get the storage prefix for a specific mode
   */
  private getModePrefix(mode: GameMode): string {
    switch (mode) {
      case 'practice':
        return 'babblegram_practice_';
      case 'custom':
        return 'babblegram_custom_';
      case 'daily':
      default:
        return 'babblegram_daily_';
    }
  }

  /**
   * Get current game mode from storage
   */
  private getCurrentMode(): GameMode {
    return (localStorage.getItem('babblegram_game_mode') as GameMode) || 'daily';
  }

  /**
   * One-time migration from legacy streak cookies to daily-prefixed cookies
   * Only migrates streak data, not game state data
   */
  private migrateLegacyDailyData(): void {
    const migrationKey = 'babblegram_daily_migration_completed';
    if (localStorage.getItem(migrationKey)) {
      return; // Migration already completed
    }

    console.log('[ModeStorage] Migrating legacy streak data to daily-prefixed cookies');
    
    // Only migrate streak-related data, not game state
    const legacyStreakKeys = [
      'streak',
      'max_streak', 
      'last_win_date'
    ];

    legacyStreakKeys.forEach(key => {
      const legacyKey = `babblegram_${key}`;
      const legacyValue = CookieUtils.getCookie(legacyKey);
      
      if (legacyValue) {
        const newKey = `babblegram_daily_${key}`;
        console.log(`[ModeStorage] Migrating streak data ${legacyKey} → ${newKey}`);
        CookieUtils.setCookie(newKey, legacyValue, this.getCookieOptions());
        // Keep legacy streak cookies for now in case other systems need them
      }
    });

    localStorage.setItem(migrationKey, 'true');
    console.log('[ModeStorage] Legacy streak data migration completed');
  }

  /**
   * Get the full key for a storage item in the current mode
   */
  private getKey(key: string): string {
    const mode = this.getCurrentMode();
    const prefix = this.getModePrefix(mode);
    return `${prefix}${key}`;
  }

  /**
   * Get the full key for a storage item in a specific mode
   */
  private getKeyForMode(key: string, mode: GameMode): string {
    const prefix = this.getModePrefix(mode);
    return `${prefix}${key}`;
  }

  /**
   * Generic cookie helpers bound to an explicit mode (prevents race conditions
   * when the active mode changes mid-operation)
   */
  public getCookieForMode(key: string, mode: GameMode): string | null {
    const fullKey = this.getKeyForMode(key, mode);
    const value = CookieUtils.getCookie(fullKey);
    console.log(`[ModeStorage] [for:${mode}] GET ${fullKey} = ${value}`);
    return value;
  }

  public setCookieForMode(key: string, value: string, mode: GameMode): void {
    const fullKey = this.getKeyForMode(key, mode);
    console.log(`[ModeStorage] [for:${mode}] SET ${fullKey} = ${value}`);
    CookieUtils.setCookie(fullKey, value, this.getCookieOptions());
  }

  public removeCookieForMode(key: string, mode: GameMode): void {
    const fullKey = this.getKeyForMode(key, mode);
    console.log(`[ModeStorage] [for:${mode}] REMOVE ${fullKey}`);
    CookieUtils.deleteCookie(fullKey);
  }

  /**
   * Cookie operations with mode isolation
   */
  private getCookieOptions() {
    const isProduction = window.location.protocol === 'https:';
    const isLovablePreview = window.location.hostname.includes('lovable.dev');
    
    return {
      path: '/',
      secure: isProduction && !isLovablePreview,
      sameSite: (isProduction && !isLovablePreview) ? 'strict' as const : 'lax' as const,
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
    };
  }

  setCookie(key: string, value: string): void {
    // Ensure migration happens before any daily mode cookie operations
    if (this.getCurrentMode() === 'daily') {
      this.migrateLegacyDailyData();
    }
    
    const fullKey = this.getKey(key);
    console.log(`[ModeStorage] Setting cookie ${fullKey} = ${value} (mode: ${this.getCurrentMode()})`);
    CookieUtils.setCookie(fullKey, value, this.getCookieOptions());
  }

  getCookie(key: string): string | null {
    // Ensure migration happens before any daily mode cookie operations
    if (this.getCurrentMode() === 'daily') {
      this.migrateLegacyDailyData();
    }
    
    const fullKey = this.getKey(key);
    const value = CookieUtils.getCookie(fullKey);
    console.log(`[ModeStorage] Getting cookie ${fullKey} = ${value} (mode: ${this.getCurrentMode()})`);
    return value;
  }

  removeCookie(key: string): void {
    const fullKey = this.getKey(key);
    console.log(`[ModeStorage] Removing cookie ${fullKey} (mode: ${this.getCurrentMode()})`);
    CookieUtils.deleteCookie(fullKey);
  }

  /**
   * Game state operations
   */
  getGameStatus(): GameStatus | null {
    const status = this.getCookie('game_status');
    return status as GameStatus | null;
  }

  setGameStatus(status: GameStatus): void {
    this.setCookie('game_status', status);
  }

  getCurrentWord(): string | null {
    return this.getCookie('current_word');
  }

  setCurrentWord(word: string): void {
    this.setCookie('current_word', word);
  }

  getCurrentLevel(): number | null {
    const level = this.getCookie('current_level');
    return level ? parseInt(level) : null;
  }

  setCurrentLevel(level: number): void {
    this.setCookie('current_level', level.toString());
  }

  getGuessHistory(): GuessHistory | null {
    const history = this.getCookie('guess_history');
    try {
      return history ? JSON.parse(history) : null;
    } catch {
      return null;
    }
  }

  setGuessHistory(history: GuessHistory): void {
    this.setCookie('guess_history', JSON.stringify(history));
  }

  getLevelAttempts(): number[] | null {
    const attempts = this.getCookie('level_attempts');
    try {
      return attempts ? JSON.parse(attempts) : null;
    } catch {
      return null;
    }
  }

  setLevelAttempts(attempts: number[]): void {
    this.setCookie('level_attempts', JSON.stringify(attempts));
  }

  getKeyboardStatus(): KeyboardStatus | null {
    const status = this.getCookie('keyboard_status');
    try {
      return status ? JSON.parse(status) : null;
    } catch {
      return null;
    }
  }

  setKeyboardStatus(status: KeyboardStatus): void {
    this.setCookie('keyboard_status', JSON.stringify(status));
  }

  getPreviousWord(): PreviousWord | null {
    const word = this.getCookie('previous_word');
    try {
      return word ? JSON.parse(word) : null;
    } catch {
      return null;
    }
  }

  setPreviousWord(word: PreviousWord): void {
    this.setCookie('previous_word', JSON.stringify(word));
  }

  getPersistenceDate(): string | null {
    return this.getCookie('game_persistence_date');
  }

  setPersistenceDate(date: string): void {
    this.setCookie('game_persistence_date', date);
  }

  getLastPlayed(): string | null {
    return this.getCookie('last_played');
  }

  setLastPlayed(date: string): void {
    this.setCookie('last_played', date);
  }

  /**
   * Streak operations (mode-specific)
   */
  getStreak(): number {
    const streak = this.getCookie('streak');
    return streak ? parseInt(streak) : 0;
  }

  setStreak(streak: number): void {
    this.setCookie('streak', streak.toString());
  }

  getMaxStreak(): number {
    const streak = this.getCookie('max_streak');
    return streak ? parseInt(streak) : 0;
  }

  setMaxStreak(streak: number): void {
    this.setCookie('max_streak', streak.toString());
  }

  getLastWinDate(): string | null {
    return this.getCookie('last_win_date');
  }

  setLastWinDate(date: string): void {
    this.setCookie('last_win_date', date);
  }

  getFailedLevel(): number | null {
    const failed = this.getCookie('failed_level');
    return failed ? parseInt(failed) : null;
  }

  setFailedLevel(level: number): void {
    this.setCookie('failed_level', level.toString());
  }

  /**
   * Clear all game state for current mode
   */
  clearModeState(): void {
    const mode = this.getCurrentMode();
    console.log(`[ModeStorage] Clearing all state for mode: ${mode}`);
    this.clearModeStateForMode(mode);
  }

  /**
   * Clear all game state for an EXPLICIT mode (avoids race conditions with current mode)
   * IMPORTANT: Does NOT clear streak data - streaks persist across day resets
   */
  clearModeStateForMode(mode: GameMode): void {
    console.log(`[ModeStorage] Clearing game state for explicit mode: ${mode} (preserving streaks)`);

    const gameStateKeys = [
      'game_status',
      'current_word', 
      'current_level',
      'current_guess',
      'keyboard_status',
      'game_persistence_date',
      'is_transitioning',
      'completed_levels',
      'guess_history',
      'level_attempts',
      'failed_level',
      'previous_word',
      'game_completion_state',
      'last_played'
      // CRITICAL: Do NOT include streak, max_streak, or last_win_date here.
      // Streaks must persist across day resets and only change on game completion.
    ];

    gameStateKeys.forEach(key => {
      this.removeCookieForMode(key, mode);
    });
    
    console.log(`[ModeStorage] Game state cleared for mode: ${mode}, streak data preserved`);
  }

  /**
   * Clear only shared keys that can cause cross-mode contamination
   * Does NOT clear mode-specific state to maintain mode independence
   */
  clearCrossModeKeys(): void {
    console.log('[ModeStorage] Clearing only truly shared contamination keys');
    
    // CRITICAL: Only clear UI overlay states that should not persist
    // DO NOT clear any game state, completion state, or mode-specific data
    const sharedContaminationKeys = [
      'babblegram_force_game_reset'
    ];
    
    sharedContaminationKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log('[ModeStorage] Preserved all mode-specific state for complete independence');
  }

  /**
   * Validate that we're accessing the correct mode's data
   */
  validateModeAccess(expectedMode?: GameMode): boolean {
    const currentMode = this.getCurrentMode();
    if (expectedMode && currentMode !== expectedMode) {
      console.warn(`[ModeStorage] Mode mismatch! Expected: ${expectedMode}, Current: ${currentMode}`);
      return false;
    }
    return true;
  }

  /**
   * Get state info for debugging
   */
  getModeStateInfo() {
    const mode = this.getCurrentMode();
    return {
      currentMode: mode,
      prefix: this.getModePrefix(mode),
      gameStatus: this.getGameStatus(),
      currentWord: this.getCurrentWord(),
      currentLevel: this.getCurrentLevel(),
      hasGuessHistory: !!this.getGuessHistory(),
      streak: this.getStreak(),
      maxStreak: this.getMaxStreak(),
      timestamp: new Date().toISOString()
    };
  }
}

export const modeAwareStorage = ModeAwareStorageManager.getInstance();